import React from "react";
import { Link } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import NorthEastRoundedIcon from "@mui/icons-material/NorthEastRounded";
import Footer from "./Footer";
import "./Home.css";

const skillLabels = {
  GenerativeAI: "Generative AI",
  OS: "Operating systems",
  CloudServices: "Cloud services",
  programming: "Programming",
  mlops: "MLOps",
  mltools: "Machine learning",
  database: "Data platforms",
  viz: "Visualisation",
  aitech: "AI technologies",
};

function Home({ data }) {
  if (!data?.main || !data?.resume) return null;

  const { main, projects = [], resume } = data;
  const featuredProjects = projects.slice(0, 4);

  return (
    <main className="Home">
      <header id="home" className="home-hero">
        <div className="hero-scrim" aria-hidden="true" />
        <div className="hero-content page-shell">
          <p className="hero-kicker">Data science leader · AI builder</p>
          <h1>{main.namelong}</h1>
          <p className="hero-role">Ph.D. · Head of Data and AI</p>
          <p className="hero-intro">
            Turning complex data into decisions that move businesses forward.
          </p>
          <div className="hero-actions">
            <a className="button button--light" href={`mailto:${main.email}`}>
              Work with me <ArrowForwardRoundedIcon fontSize="small" />
            </a>
            <a className="text-link text-link--light" href="#work">
              Explore my work
            </a>
          </div>
        </div>
        <a className="hero-scroll" href="#work" aria-label="Scroll to selected work">
          <span>Scroll</span>
          <i aria-hidden="true" />
        </a>
      </header>

      <section id="work" className="home-section work-section">
        <div className="page-shell">
          <div className="section-heading">
            <div>
              <p className="section-kicker">01 / Selected work</p>
              <h2>From strategy to<br />production.</h2>
            </div>
            <div className="section-heading__side">
              <p>
                Data products built for real organisations, real users and
                measurable outcomes.
              </p>
              <Link className="text-link" to="/projects">
                View all projects <ArrowForwardRoundedIcon fontSize="small" />
              </Link>
            </div>
          </div>

          <div className="project-grid">
            {featuredProjects.map((project, index) => (
              <Link className="project-card" to="/projects" key={project.name}>
                <div className="project-card__meta">
                  <span>0{index + 1}</span>
                  <span>{project.start_date}</span>
                </div>
                <h3>{project.name}</h3>
                <p>{project.description}</p>
                <div className="project-card__footer">
                  <span>{project.where}</span>
                  <NorthEastRoundedIcon aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="home-section about-section">
        <div className="page-shell about-grid">
          <div>
            <p className="section-kicker section-kicker--dark">02 / About</p>
            <h2>Technical depth.<br />Commercial clarity.</h2>
          </div>
          <div className="about-copy">
            <p className="about-copy__lead">
              I build the bridge between ambitious ideas and dependable data
              products.
            </p>
            <p>{main.longintro}</p>
            <a className="text-link text-link--light" href={`mailto:${main.email}`}>
              Start a conversation <ArrowForwardRoundedIcon fontSize="small" />
            </a>
          </div>
        </div>
      </section>

      <section id="experience" className="home-section experience-section">
        <div className="page-shell">
          <div className="section-heading section-heading--compact">
            <div>
              <p className="section-kicker">03 / Experience</p>
              <h2>A career built<br />around impact.</h2>
            </div>
            <p className="experience-summary">
              Over a decade across research, data science, machine learning
              and technical leadership.
            </p>
          </div>

          <div className="experience-layout">
            <div className="timeline" aria-label="Work experience">
              {resume.work.map((role) => (
                <article className="timeline-row" key={`${role.company}-${role.years}`}>
                  <div className="timeline-row__date">{role.years}</div>
                  <div>
                    <h3>{role.company}</h3>
                    <p className="timeline-row__role">{role.title} · {role.where}</p>
                    <p>{role.description}</p>
                  </div>
                </article>
              ))}
            </div>

            <aside className="education-panel">
              <p className="mini-label">Education</p>
              {resume.education.map((item) => (
                <div className="education-item" key={item.school}>
                  <span>{item.graduated}</span>
                  <h3>{item.degree}</h3>
                  <p>{item.school}</p>
                </div>
              ))}
            </aside>
          </div>
        </div>
      </section>

      <section id="skills" className="home-section skills-section">
        <div className="page-shell">
          <div className="skills-intro">
            <p className="section-kicker section-kicker--dark">04 / Capabilities</p>
            <h2>The toolkit changes.<br />The thinking endures.</h2>
          </div>
          <div className="skills-list">
            {Object.entries(resume.skills).map(([key, skill], index) => (
              <div className="skill-row" key={key}>
                <span className="skill-row__number">0{index + 1}</span>
                <h3>{skillLabels[key] || key}</h3>
                <p>{skill.list}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer data={main} />
    </main>
  );
}

export default Home;
